// Export/import of the modular Z/X report layout so a tenant's configuration can be copied to
// another tenant without re-clicking it. The exported file wraps the config in a small envelope
// carrying provenance (which tenant, when) — the envelope is informational only; the config it
// carries is the single source of truth and is re-validated on import.
//
// Imports are deliberately forgiving about the wrapper and strict about the payload: a bare
// config object is accepted too (someone may hand over just the inner object), but whatever is
// found must pass zReportLayoutConfigSchema and then mergeZReportLayoutConfig, which drops keys
// absent from this build's catalog and back-fills anything missing. That keeps a file exported
// from an older or newer build from corrupting the layout here.
import { zReportLayoutConfigSchema } from 'shared-models/browser';
import type { ZReportLayoutConfig } from 'shared-models/browser';
import { mergeZReportLayoutConfig } from './z-report-layout.catalog';

export const Z_REPORT_LAYOUT_EXPORT_KIND = 'communicatepos.z-report-layout';

export type ZReportLayoutExportFile = {
  kind: typeof Z_REPORT_LAYOUT_EXPORT_KIND;
  version: 1;
  exportedAt: string;
  tenantId?: string;
  config: ZReportLayoutConfig;
};

export type ParseResult =
  | { ok: true; config: ZReportLayoutConfig }
  | { ok: false; error: string };

export function buildLayoutExportFile(
  config: ZReportLayoutConfig | null,
  options: { tenantId?: string; exportedAt: Date },
): ZReportLayoutExportFile {
  return {
    kind: Z_REPORT_LAYOUT_EXPORT_KIND,
    version: 1,
    exportedAt: options.exportedAt.toISOString(),
    ...(options.tenantId ? { tenantId: options.tenantId } : {}),
    // Normalize on the way out so the file always holds a complete, catalog-valid config even if
    // the tenant never saved one (null == "using defaults").
    config: mergeZReportLayoutConfig(config),
  };
}

// Filenames land in a downloads folder alongside exports from other tenants, so the tenant and
// date are part of the name rather than left to the browser's "(1)" disambiguation.
export function buildLayoutExportFilename(options: { tenantId?: string; exportedAt: Date }): string {
  const datePart = options.exportedAt.toISOString().slice(0, 10);
  const tenantPart = options.tenantId ? `-${sanitizeFilenamePart(options.tenantId)}` : '';
  return `z-report-layout${tenantPart}-${datePart}.json`;
}

function sanitizeFilenamePart(value: string): string {
  const cleaned = value.trim().replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned.slice(0, 40) || 'tenant';
}

export function parseLayoutImportFile(text: string): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'הקובץ אינו קובץ JSON תקין.' };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'הקובץ אינו קובץ הגדרות של דוח מודולרי.' };
  }

  const record = parsed as Record<string, unknown>;
  // An envelope from this feature, or a bare config someone extracted from one.
  const candidate = 'config' in record ? record.config : record;

  if (
    'kind' in record
    && typeof record.kind === 'string'
    && record.kind !== Z_REPORT_LAYOUT_EXPORT_KIND
  ) {
    return { ok: false, error: 'הקובץ שייך לסוג הגדרות אחר.' };
  }

  const result = zReportLayoutConfigSchema.safeParse(candidate);
  if (!result.success) {
    return { ok: false, error: 'מבנה ההגדרות בקובץ אינו תקין.' };
  }

  const merged = mergeZReportLayoutConfig(result.data);
  if (merged.entries.length === 0) {
    return { ok: false, error: 'הקובץ אינו מכיל הגדרות מתאימות לגרסה זו.' };
  }

  return { ok: true, config: merged };
}
